import { useEffect } from 'react'

// Accepted components → pages import (precedent: citationSource reads library
// helpers): the social image proxy and its blob cache are owned by the Library.
import { useSocialItemImage } from '@/pages/KnowledgeHubPage/social/useSocialItemImage'

import { CitationImage, sourceImageTokens, sourceImageUrl } from './citationSource'

export interface CitationImageViewProps {
  image: CitationImage
  /** The source name: the picture shows what the source is, nothing more. */
  alt: string
  className?: string
  testId: string
  onLoad?: () => void
  /** Called once the picture cannot be shown, so the caller can drop its box. */
  onError?: () => void
}

interface SocialPathImageProps {
  path: string
  alt: string
  className?: string
  testId: string
  onLoad?: () => void
  onError?: () => void
}

function SocialPathImage(props: SocialPathImageProps) {
  const { path, alt, className, testId, onError, onLoad } = props

  const { src, hasError } = useSocialItemImage(path)

  useEffect(() => {
    if (hasError) {
      onError?.()
    }
  }, [hasError, onError])

  if (!src) {
    return null
  }

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={className}
      data-testid={testId}
      onLoad={onLoad}
      onError={onError}
    />
  )
}

/**
 * A saved canvas picture, loaded straight from the API host: the route answers
 * `immutable`, so the browser keeps it, and lazy loading waits for the
 * viewport. The parent reserves the box, so nothing shifts when it lands.
 */
function CachedSourceImage(props: SocialPathImageProps) {
  const { path, alt, className, testId, onError, onLoad } = props
  const src = sourceImageUrl(path)
  if (!src) {
    return null
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={className}
      data-testid={testId}
      onLoad={onLoad}
      onError={onError}
    />
  )
}

/**
 * The one renderer for a citation's picture. A `url` image loads directly
 * with no referrer; a social image goes through the authenticated proxy as a
 * blob. A failed load reports to `onError` and the caller removes the box.
 */
export function CitationImageView(props: CitationImageViewProps) {
  const { image, alt, className, testId, onError, onLoad } = props

  if (image.kind === 'url' && sourceImageTokens(image.src)) {
    return <CachedSourceImage path={image.src} alt={alt} className={className} testId={testId} onError={onError} onLoad={onLoad} />
  }

  if (image.kind === 'socialPath') {
    return <SocialPathImage path={image.path} alt={alt} className={className} testId={testId} onError={onError} onLoad={onLoad} />
  }

  return (
    <img
      src={image.src}
      alt={alt}
      referrerPolicy="no-referrer"
      loading="lazy"
      className={className}
      data-testid={testId}
      onLoad={onLoad}
      onError={onError}
    />
  )
}
